import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, CalendarCheck, Loader2, MapPin, CheckCircle2 } from "lucide-react";
import { useLang } from "@/i18n";
import Footer from "@/components/site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RendezVousPage() {
  const { t } = useLang();
  const [form, setForm] = useState({ nom: "", contact: "", sujet: "Paiement inscription", sujetAutre: "" });
  const [date, setDate] = useState("");
  const [heure, setHeure] = useState("");
  const [planning, setPlanning] = useState({ heures: [], fermes: [] });
  const [adresse, setAdresse] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/rdv/planning`).then((r) => setPlanning(r.data)).catch(() => {});
    axios.get(`${API}/settings`).then((r) => setAdresse(r.data.bureau_adresse)).catch(() => {});
  }, []);

  const isFerme = (h) => date && (planning.fermes.includes(`${date}|${h}`) || planning.fermes.includes(`${date}|*`));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const sujet = form.sujet === "Autre" ? form.sujetAutre.trim() || "Autre" : form.sujet;
      await axios.post(`${API}/rdv/autre`, {
        nom: form.nom, contact: form.contact, sujet, rdv_date: date, rdv_heure: heure,
      });
      setConfirmed(true);
      toast.success(t("rdv.toast.ok"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("office.toast.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="rdv-libre-page" className="grain bg-[#0A0A0C] text-white min-h-screen">
      <header className="border-b border-[#27272A] bg-[#0A0A0C]/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="rdv-libre-back" className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#C5A059] transition-colors text-xs uppercase tracking-[0.2em]">
            <ArrowLeft size={15} /> {t("eventpage.back")}
          </Link>
          <img src="/assets/logo-dark-sm.png" alt="DREAM2REALITY Football Agency" className="h-10 w-auto" />
        </div>
      </header>

      <main className="max-w-xl mx-auto px-6 py-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.4em] mb-6">{t("rdv.eyebrow")}</p>
          <h1 className="font-display text-4xl lg:text-5xl uppercase font-bold leading-[0.95]">
            {t("rdv.title.a")} <span className="text-[#C5A059]">{t("rdv.title.b")}</span>
          </h1>
          <p className="text-sm text-[#A1A1AA] mt-5 leading-relaxed">{t("rdv.text")}</p>
          {adresse && (
            <div className="mt-6 flex items-start gap-3 border border-[#27272A] bg-[#121214] px-5 py-4" data-testid="rdv-libre-adresse">
              <MapPin size={18} className="text-[#C5A059] shrink-0 mt-0.5" />
              <p className="text-sm text-[#E4E4E7] font-medium leading-relaxed">{adresse}</p>
            </div>
          )}
        </motion.div>

        {confirmed ? (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="mt-10 border border-[#C5A059]/40 bg-[#121214] p-10 text-center" data-testid="rdv-libre-confirmed">
            <CheckCircle2 size={40} className="text-[#C5A059] mx-auto" strokeWidth={1.5} />
            <h2 className="font-display text-2xl uppercase font-bold mt-6">{t("rdv.done.t")}</h2>
            <p className="text-sm text-[#A1A1AA] mt-3">{date.split("-").reverse().join("/")} · {heure} — {form.sujet === "Autre" ? form.sujetAutre : form.sujet}</p>
            <p className="text-xs text-[#71717A] mt-4 leading-relaxed">{t("rdv.done.x")}</p>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="mt-10 border border-[#27272A] bg-[#121214] p-8 space-y-7" data-testid="rdv-libre-form">
            <input required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder={t("rdv.f.nom")} data-testid="rdv-libre-nom" className="input-editorial" />
            <input required value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder={t("rdv.f.contact")} data-testid="rdv-libre-contact" className="input-editorial" />
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#71717A]">{t("rdv.f.sujet")}</label>
              <select value={form.sujet} onChange={(e) => setForm({ ...form, sujet: e.target.value })} data-testid="rdv-libre-sujet" className="input-editorial mt-1">
                <option value="Paiement inscription">{t("rdv.sujet.paiement")}</option>
                <option value="Autre">{t("rdv.sujet.autre")}</option>
              </select>
            </div>
            {form.sujet === "Autre" && (
              <input required value={form.sujetAutre} onChange={(e) => setForm({ ...form, sujetAutre: e.target.value })} placeholder={t("rdv.f.sujetAutre")} data-testid="rdv-libre-sujet-autre" className="input-editorial" />
            )}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#71717A]">{t("office.date")}</label>
              <input type="date" required value={date} onChange={(e) => { setDate(e.target.value); setHeure(""); }} data-testid="rdv-libre-date" className="input-editorial [color-scheme:dark]" />
            </div>
            {date && (
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#71717A]">{t("office.time")}</label>
                <div className="grid grid-cols-3 gap-3 mt-2" data-testid="rdv-libre-slots">
                  {(planning.heures.length ? planning.heures : ["09h00", "10h00", "11h00", "14h00", "15h00", "16h00"]).map((h) => {
                    const ferme = isFerme(h);
                    return (
                      <button
                        key={h}
                        type="button"
                        disabled={ferme}
                        onClick={() => setHeure(h)}
                        data-testid={`rdv-libre-slot-${h}`}
                        className={`py-3 text-sm font-medium border transition-colors duration-300 ${
                          ferme
                            ? "border-red-900/40 text-red-900/60 cursor-not-allowed"
                            : heure === h
                              ? "border-[#C5A059] bg-[#C5A059]/10 text-[#C5A059]"
                              : "border-[#27272A] text-[#A1A1AA] hover:border-[#C5A059] hover:text-[#C5A059]"
                        }`}
                      >
                        {h}
                        {ferme && <span className="block text-[8px] uppercase tracking-widest">{t("office.closed")}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <button type="submit" disabled={loading || !heure} data-testid="rdv-libre-submit"
              className="w-full flex items-center justify-center gap-3 bg-[#C5A059] text-black px-8 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors duration-300 disabled:opacity-60">
              {loading ? <Loader2 size={15} className="animate-spin" /> : <CalendarCheck size={15} />}
              {t("rdv.submit")}
            </button>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
}
